import React, { memo as reactMemo } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemButtonProps,
  ListItemIcon,
  ListItemText,
  styled
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Link,
  TranslatedText,
  useToggle,
  useTranslator
} from '@ianesthesie-kit/core';
import { evaluate, isLeafNav, isParentNav, NavItemData } from '@/config';
import { themeColors } from '@ianesthesie-kit/config';
import { Hospital } from '@ianesthesie-kit/services';
import { LoadingOverlay } from '@/components/patcher';

const variants = {
  open: {
    x: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 }
    }
  },
  closed: {
    x: -30,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 }
    }
  }
};

const UnstyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const StyledListItemIcon = styled(ListItemIcon)`
  width: 24px;
  height: 24px;
  min-width: unset;
  margin-right: 8px;
  svg {
    color: ${(p) => p.theme.palette.text.secondary};
  }
`;

const StyledListItemButton = styled(ListItemButton)<ListItemButtonProps<any>>`
  padding: 4px 12px;
  margin: 2px auto;
  width: 90%;
  border-radius: 8px;
  z-index: 2;
`;

const SelectionBg = styled(motion.div)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${(p) => p.theme.palette.primary.xLight};
  z-index: 1;
  width: 90%;
  margin: auto;
  border-radius: 8px;
`;

const NewBadge = styled(motion.div)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  padding: 2px 6px;
  background: ${themeColors.red[600]};
  color: white;
  font-weight: 800;
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  border-radius: 8px;
`;

const isAbsoluteUrl = (url = '') => /^https?:\/\/.*/.test(url);

const ACTIVE_SCALE = 1.05;

const LeafNavComp = ({
  navItem,
  currentRoute,
  showNewBadge,
  onAfterClick
}: NavigationItemProps) => {
  const translate = useTranslator();
  const path = isLeafNav(navItem) ? navItem.path : '';
  const active = currentRoute === path;
  const isAbsolute = isLeafNav(navItem) && isAbsoluteUrl(navItem.path);

  return (
    <Box position="relative">
      {!!showNewBadge && (
        <AnimatePresence>
          <NewBadge
            initial={{ opacity: 0, scale: 0, x: -12, y: '25%' }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
          >
            <TranslatedText id="doctor-dashboard.nav.new" />
          </NewBadge>
        </AnimatePresence>
      )}
      {active && (
        <SelectionBg
          layoutId="nav-menu-selection-bg"
          animate={{ scale: active ? ACTIVE_SCALE : 1 }}
          whileHover={{ scale: ACTIVE_SCALE }}
          whileTap={{ scale: 0.98 }}
        />
      )}
      {isAbsolute && (
        <a
          href={navItem.path}
          target="_blank"
          style={{ color: 'inherit', textDecoration: 'inherit' }}
        >
          <StyledListItemButton
            component={motion.li}
            variants={variants}
            animate={{ scale: active ? ACTIVE_SCALE : 1 }}
            whileHover={{ scale: ACTIVE_SCALE }}
            whileTap={{ scale: 0.98 }}
          >
            <StyledListItemIcon>{navItem.icon}</StyledListItemIcon>
            <ListItemText primary={translate(navItem.text)} />
          </StyledListItemButton>
        </a>
      )}
      {!isAbsolute && (
        <Box component={UnstyledLink} href={path}>
          <StyledListItemButton
            disabled={!path}
            component={motion.li}
            variants={variants}
            animate={{ scale: active ? ACTIVE_SCALE : 1 }}
            whileHover={{ scale: ACTIVE_SCALE }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onAfterClick?.(navItem)}
          >
            <StyledListItemIcon>{navItem.icon}</StyledListItemIcon>
            <ListItemText primary={translate(navItem.text)} />
          </StyledListItemButton>
        </Box>
      )}
    </Box>
  );
};

const ExpandIcon = ({ expanded }: { expanded: boolean }) => (
  <motion.span
    initial={false}
    animate={{ rotate: expanded ? 180 : 0 }}
    style={{ transformOrigin: 'center', display: 'flex', alignItems: 'center' }}
  >
    <ExpandMore />
  </motion.span>
);

type ParentNavProps = NavigationItemProps;

const ParentNavComp = ({
  navItem,
  currentRoute,
  currentHospital
}: ParentNavProps) => {
  const parentPath = isParentNav(navItem) ? navItem.parentPath : '';
  const active = !!parentPath && currentRoute.startsWith(parentPath);
  const { toggle, toggledOn: expanded } = useToggle(active);
  const translate = useTranslator();

  const prefixPath = (nav: NavItemData): NavItemData => {
    if (isParentNav(nav)) {
      return {
        ...nav,
        parentPath: parentPath.concat(nav.parentPath)
      };
    }
    if (isAbsoluteUrl(nav.path)) {
      return nav;
    }
    return {
      ...nav,
      path: parentPath.concat(nav.path)
    };
  };

  return (
    <>
      <StyledListItemButton
        onClick={() => toggle()}
        component={motion.li}
        variants={variants}
        animate={{
          backgroundColor: active
            ? themeColors.indigo[50]
            : 'rgba(255 255 255 / 0)',
          scale: active ? ACTIVE_SCALE : 1
        }}
      >
        <StyledListItemIcon>{navItem.icon}</StyledListItemIcon>
        <ListItemText primary={translate(navItem.text)} />
        <ExpandIcon expanded={expanded} />
      </StyledListItemButton>
      <List
        layout="size"
        disablePadding
        component={motion.div}
        animate={{ height: expanded ? 'auto' : 0 }}
        style={{ overflow: 'hidden' }}
      >
        {isParentNav(navItem) &&
          navItem.childNavs.map((nav) => (
            <NavigationItem
              key={`${isParentNav(nav) ? nav.parentPath : nav.path}_${
                nav.text
              }`}
              navItem={prefixPath(nav)}
              currentRoute={currentRoute}
              currentHospital={currentHospital}
            />
          ))}
      </List>
    </>
  );
};

export interface NavigationItemProps {
  navItem: NavItemData;
  currentRoute: string;
  currentHospital: Hospital | undefined;
  isEvaluatingPermissions?: boolean;
  showNewBadge?: boolean;
  onAfterClick?: (navItem: NavItemData) => void;
}

const UnmemoizedNavigationItem = (props: NavigationItemProps) => {
  const isEnabled =
    !props.navItem.isEnabled ||
    (!props.isEvaluatingPermissions &&
      evaluate(props.navItem.isEnabled, props.currentHospital));

  if (props.isEvaluatingPermissions) {
    return <LoadingOverlay />;
  }

  if (!isEnabled) {
    return null;
  }

  if (isParentNav(props.navItem)) {
    return <ParentNavComp {...props} />;
  }

  return <LeafNavComp {...props} />;
};

export const NavigationItem = reactMemo(
  UnmemoizedNavigationItem,
  (prevProps, nextProps) => {
    // check for translation updates
    if (prevProps.navItem.text !== nextProps.navItem.text) {
      return false;
    }

    if (
      isLeafNav(prevProps.navItem) &&
      isLeafNav(nextProps.navItem) &&
      prevProps.navItem.path !== nextProps.navItem.path
    ) {
      return false;
    }

    return (
      prevProps.currentRoute === nextProps.currentRoute &&
      prevProps.isEvaluatingPermissions === nextProps.isEvaluatingPermissions &&
      prevProps.currentHospital?.id === nextProps.currentHospital?.id
    );
  }
);

NavigationItem.displayName = 'NavigationItem';
